// Cart Manager - Handles shopping cart state and persistence
export interface CartItem {
  id: string;
  productId: string;
  name: string;
  price: number;
  quantity: number;
  options?: Record<string, any>;
  image?: string;
}

export interface Cart {
  items: CartItem[];
  subtotal: number;
  shipping: number;
  total: number;
}

type AuthManager = {
  isAuthenticated(): boolean;
  getIdToken(): Promise<string | null>;
};

export class CartManager {
  private cart: Cart;
  private listeners: ((cart: Cart) => void)[] = [];
  private storageKey = "novi-prints-cart";
  private authManager: AuthManager | null = null;

  constructor(authManager?: AuthManager) {
    this.authManager = authManager || null;
    this.cart = this.loadCart();
    this.init();
  }

  init(): void {
    // Listen for storage changes (sync across tabs)
    window.addEventListener("storage", (e) => {
      if (e.key === this.storageKey) {
        this.cart = this.loadCart();
        this.notifyListeners();
      }
    });

    // Listen for beforeunload to save cart
    window.addEventListener("beforeunload", () => {
      this.saveCart();
    });
  }

  // Add item to cart
  addItem(
    productId: string,
    name: string,
    price: number,
    quantity: number = 1,
    options?: Record<string, any>,
    image?: string
  ): void {
    const existingItem = this.cart.items.find(
      (item) =>
        item.productId === productId &&
        JSON.stringify(item.options) === JSON.stringify(options)
    );

    if (existingItem) {
      existingItem.quantity += quantity;
    } else {
      const newItem: CartItem = {
        id: this.generateItemId(),
        productId,
        name,
        price,
        quantity,
        options,
        image,
      };
      this.cart.items.push(newItem);
    }

    this.updateTotals();
    this.saveCart();
    this.notifyListeners();
  }

  // Remove item from cart
  removeItem(itemId: string): void {
    this.cart.items = this.cart.items.filter((item) => item.id !== itemId);
    this.updateTotals();
    this.saveCart();
    this.notifyListeners();
  }

  // Update item quantity
  updateQuantity(itemId: string, quantity: number): void {
    const item = this.cart.items.find((item) => item.id === itemId);
    if (item) {
      if (quantity <= 0) {
        this.removeItem(itemId);
      } else {
        item.quantity = quantity;
        this.updateTotals();
        this.saveCart();
        this.notifyListeners();
      }
    }
  }

  // Clear cart
  clearCart(): void {
    this.cart.items = [];
    this.updateTotals();
    this.saveCart();
    this.notifyListeners();
  }

  // Get cart
  getCart(): Cart {
    return { ...this.cart };
  }

  // Get cart items
  getItems(): CartItem[] {
    return [...this.cart.items];
  }

  // Get item count
  getItemCount(): number {
    return this.cart.items.reduce((total, item) => total + item.quantity, 0);
  }

  // Check if cart is empty
  isEmpty(): boolean {
    return this.cart.items.length === 0;
  }

  // Add listener
  onCartUpdated(listener: (cart: Cart) => void): void {
    this.listeners.push(listener);
  }

  // Subscribe to cart changes (alias for onCartUpdated)
  subscribe(listener: (cart: Cart) => void): void {
    this.onCartUpdated(listener);
  }

  // Remove listener
  removeListener(listener: (cart: Cart) => void): void {
    const index = this.listeners.indexOf(listener);
    if (index > -1) {
      this.listeners.splice(index, 1);
    }
  }

  // Notify listeners
  private notifyListeners(): void {
    this.listeners.forEach((listener) => {
      try {
        listener(this.getCart());
      } catch (error) {
        console.error("Error in cart listener:", error);
      }
    });
  }

  // Update totals
  private updateTotals(): void {
    this.cart.subtotal = this.cart.items.reduce(
      (total, item) => total + item.price * item.quantity,
      0
    );
    this.cart.shipping = this.cart.subtotal > 0 ? 49 : 0; // 49 SEK shipping
    this.cart.total = this.cart.subtotal + this.cart.shipping;
  }

  // Generate unique item ID
  private generateItemId(): string {
    return "item_" + Date.now() + "_" + Math.random().toString(36).substr(2, 9);
  }

  // Load cart from localStorage
  private loadCart(): Cart {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored) {
        const cart = JSON.parse(stored);
        // Validate cart structure
        if (cart.items && Array.isArray(cart.items)) {
          // ensure numeric fields exist
          cart.subtotal = Number(cart.subtotal || 0);
          cart.shipping = Number(cart.shipping || 0);
          cart.total = Number(cart.total || 0);
          this.cart = cart;
          this.updateTotals();
          return this.cart;
        }
      }
    } catch (error) {
      console.error("Failed to load cart from localStorage:", error);
    }

    // Return empty cart
    return {
      items: [],
      subtotal: 0,
      shipping: 0,
      total: 0,
    };
  }

  // Save cart to localStorage
  private saveCart(): void {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.cart));
      localStorage.setItem(
        `${this.storageKey}_updated_at`,
        Date.now().toString()
      );
      // Auto-sync to server if authenticated
      if (this.authManager && this.authManager.isAuthenticated()) {
        // Debounce sync calls - don't sync on every change
        this.debouncedSync();
      }
    } catch (error) {
      console.error("Failed to save cart to localStorage:", error);
    }
  }

  private syncTimeout: number | null = null;
  private debouncedSync(): void {
    if (this.syncTimeout !== null) {
      clearTimeout(this.syncTimeout);
    }
    // Sync after 1 second of inactivity
    this.syncTimeout = window.setTimeout(() => {
      this.syncWithServer();
      this.syncTimeout = null;
    }, 1000);
  }

  // Sync cart with server (for logged-in users)
  async syncWithServer(): Promise<void> {
    if (!this.authManager || !this.authManager.isAuthenticated()) {
      return; // Skip sync if not authenticated
    }

    try {
      const token = await this.authManager.getIdToken();
      if (!token) {
        console.warn("Failed to get auth token for cart sync");
        return;
      }

      // Convert cart items to server format
      const serverCart = {
        items: this.cart.items.map((item) => ({
          id: item.id,
          product_id: item.productId,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          options: item.options || {},
          image: item.image || "",
        })),
      };

      const response = await fetch("/api/cart", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(serverCart),
      });

      if (!response.ok) {
        throw new Error(`Failed to sync cart: ${response.status}`);
      }
    } catch (error) {
      console.error("Failed to sync cart with server:", error);
      // Don't throw - cart should still work locally
    }
  }

  // Load cart from server (for logged-in users)
  async loadFromServer(): Promise<void> {
    if (!this.authManager || !this.authManager.isAuthenticated()) {
      return; // Skip load if not authenticated
    }

    try {
      const token = await this.authManager.getIdToken();
      if (!token) {
        console.warn("Failed to get auth token for cart load");
        return;
      }

      const response = await fetch("/api/cart", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          // No cart on server yet, that's okay
          return;
        }
        throw new Error(`Failed to load cart: ${response.status}`);
      }

      const serverCart = await response.json();

      // Convert server cart format to local format
      if (serverCart.items && serverCart.items.length > 0) {
        // Merge server cart with local cart (prefer server if more recent)
        const serverUpdatedAt = serverCart.updated_at || 0;
        const localCart = this.loadCart();
        const localUpdatedAt = localStorage.getItem(
          `${this.storageKey}_updated_at`
        )
          ? parseInt(localStorage.getItem(`${this.storageKey}_updated_at`) || "0")
          : 0;

        // Use server cart if it's newer, otherwise merge
        if (serverUpdatedAt > localUpdatedAt) {
          this.cart.items = serverCart.items.map((item: any) => ({
            id: item.id,
            productId: item.product_id,
            name: item.name,
            price: item.price,
            quantity: item.quantity,
            options: item.options || {},
            image: item.image || "",
          }));
          this.updateTotals();
          this.saveCart();
          this.notifyListeners();
        } else {
          // Local cart is newer, sync it to server instead
          await this.syncWithServer();
        }
      }
    } catch (error) {
      console.error("Failed to load cart from server:", error);
      // Don't throw - cart should still work locally
    }
  }

  // Export cart for checkout
  exportForCheckout(): {
    items: Array<{
      productId: string;
      quantity: number;
      price: number;
      options?: Record<string, any>;
    }>;
    subtotal: number;
    shipping: number;
    total: number;
  } {
    return {
      items: this.cart.items.map((item) => ({
        productId: item.productId,
        quantity: item.quantity,
        price: item.price,
        options: item.options,
      })),
      subtotal: this.cart.subtotal,
      shipping: this.cart.shipping,
      total: this.cart.total,
    };
  }
}
